import React from "react";
import UnitHierarchy from "../../UnitHierarchy";
import { Col, Form } from "antd";
import { useEffect, useState } from "react";
import { FormItemArea } from "./FormItem.styled";
import FormTreeSelect from "./FormTreeSelect";
import {
  DropdownGrid,
  FormCascader,
  FormCheckbox,
  FormDatePicker,
  FormInput,
  FormInputNumber,
  FormMonthYearRange,
  FormMultiSelect,
  FormRadio,
  FormRangePicker,
  FormSelect,
  FormTags,
  FormTextArea,
  // FormTextEditor,
  FormTimePicker,
  SuggestionSelect,
} from "./index";

export const FormItem = ({
  rules,
  labelCol,
  wrapperCol,
  field,
  disabled,
  ...rest
}) => {
  const [validRules, setValidationRules] = useState([]);

  const generateRules = () => {
    const fieldRules = [];
    if (rules) {
      fieldRules.push(...rules);
    }
    if (field.validation_rules) {
      try {
        fieldRules.push(...JSON.parse(field.validation_rules));
      } catch (error) {}
    }
    if (field.required) {
      fieldRules.unshift({
        required: true,
        message: `${field.label_name ?? "Field"} is required!`,
      });
    }
    if (field.input_type === "email") {
      fieldRules.push({
        type: "email",
        message: `${field.label_name} is not a valid E-mail!`,
      });
    } else if (field.input_type === "tel") {
      fieldRules.push({
        message: `${field.label_name} is not a valid Phone Number!`,
        // eslint-disable-next-line prefer-regex-literals
        pattern: new RegExp(/(^(\+88|0088)?(01){1}[3456789]{1}(\d){8})$/),
      });
    } else if (field.input_type === "number") {
      fieldRules.push({
        type: "number",
        message: `${field.label_name} is not a valid number!`,
      });
      if (field.input_expression === `^[0-9]*\\.?[0-9]{1,2}$`) {
        fieldRules.push({
          message: `${field.label_name} should be two decimal places`,
          pattern: new RegExp(field.input_expression),
        });
      }
    }
    setValidationRules(fieldRules);
  };

  useEffect(() => {
    generateRules();
  }, [field]);

  // render data
  const renderSwitch = (param) => {
    switch (param) {
      case "text":
      case "tel":
      case "email":
        return (
          <FormInput
            name={field.input_name}
            type={param}
            placeholder={field.input_placeholder ?? ""}
            disabled={disabled || field.disabled}
            {...rest}
          />
        );

      case "number":
        return (
          <FormInputNumber
            name={field.input_name}
            type={param}
            placeholder={field.input_placeholder ?? ""}
            disabled={disabled || field.disabled}
            {...rest}
          />
        );

      case "textarea":
        return (
          <FormTextArea
            name={field.input_name}
            placeholder={field.input_placeholder ?? ""}
            disabled={disabled || field.disabled}
            {...rest}
          />
        );

      // case "editor":
      //   return (
      //     <FormTextEditor
      //       name={field.input_name}
      //       placeholder={field.input_placeholder ?? ""}
      //       disabled={disabled || field.disabled}
      //       {...rest}
      //     />
      //   );

      case "month":
        return (
          <FormDatePicker
            name={field.input_name}
            type={param}
            placeholder={field.input_placeholder ?? ""}
            disabled={disabled || field.disabled}
            format="YYYY-MM"
            {...rest}
          />
        );

      case "year":
        return (
          <FormDatePicker
            name={field.input_name}
            type={param}
            placeholder={field.input_placeholder ?? ""}
            disabled={disabled || field.disabled}
            format="YYYY"
            {...rest}
          />
        );

      case "date":
        return (
          <FormDatePicker
            name={field.input_name}
            type={param}
            placeholder={field.input_placeholder ?? ""}
            disabled={disabled || field.disabled}
            selectedDate={rest.value}
            {...rest}
          />
        );

      case "time":
        return (
          <FormTimePicker
            name={field.input_name}
            type={param}
            placeholder={field.input_placeholder ?? ""}
            disabled={disabled || field.disabled}
            {...rest}
          />
        );

      case "datetime":
        return (
          <FormDatePicker
            name={field.input_name}
            type="date"
            showTime={true}
            placeholder={field.input_placeholder ?? ""}
            disabled={disabled || field.disabled}
            {...rest}
          />
        );

      case "date_range":
        return (
          <FormRangePicker
            name={field.input_name}
            showTime={true}
            placeholder={field.input_placeholder ?? ""}
            disabled={disabled || field.disabled}
            {...rest}
          />
        );

      case "month_range":
      case "year_range":
        return (
          <FormMonthYearRange
            type={param}
            name={field.input_name}
            placeholder={field.input_placeholder ?? ""}
            disabled={disabled || field.disabled}
            {...rest}
          />
        );

      case "select":
      case "dropdown":
        return (
          <FormSelect
            name={field?.input_name}
            slug={field?.dropdown_slug}
            dropdownOptions={field?.dropdown_options}
            placeholder={field.input_placeholder ?? ""}
            extra={field.extra ?? ""}
            disabled={disabled || field.disabled}
            disabledOptions={field.disabledOptions}
            {...rest}
          />
        );
      case "cascader":
        return (
          <FormCascader
            name={field?.input_name}
            slug={field?.dropdown_slug}
            dropdownOptions={field?.dropdown_options}
            placeholder={field.input_placeholder ?? ""}
            extra={field.extra ?? ""}
            disabled={disabled || field.disabled}
            {...rest}
          />
        );
      case "tags":
        return (
          <FormTags
            name={field?.input_name}
            slug={field?.dropdown_slug}
            dropdownOptions={field?.dropdown_options}
            placeholder={field.input_placeholder ?? ""}
            extra={field.extra ?? ""}
            disabled={disabled || field.disabled}
            {...rest}
          />
        );

      case "multi_select":
        return (
          <FormMultiSelect
            name={field?.input_name}
            slug={field?.dropdown_slug}
            dropdownOptions={field?.dropdown_options}
            placeholder={field.input_placeholder ?? ""}
            extra={field.extra ?? ""}
            disabled={disabled || !!field.disabled}
            {...rest}
          />
        );

      case "suggestion":
      case "autocomplete":
        return (
          <SuggestionSelect
            name={field?.input_name}
            slug={field?.dropdown_slug}
            placeholder={field.input_placeholder ?? ""}
            extra={field.extra ?? ""}
            disabled={disabled || field.disabled}
            {...rest}
          />
        );

      case "dropdown_grid":
        return (
          <DropdownGrid
            name={field?.input_name}
            slug={field?.dropdown_slug}
            label_name={field?.label_name}
            placeholder={field.input_placeholder ?? ""}
            is_multiple={!!field.multiple}
            labelKey={field.input_label ?? false}
            valueKey={field.input_value ?? false}
            displaySelected={true}
            extra={field.extra ?? ""}
            disabled={disabled || field.disabled}
            {...rest}
          />
        );
      case "checkbox":
        return (
          <FormCheckbox
            name={field.input_name ?? "field_name"}
            required={field.required ?? false}
            type={field.input_type}
            label={field.input_label}
            direction={field.direction ?? "horizontal"}
            slug={field?.dropdown_slug}
            checkBoxOptions={field.dropdown_options}
            disabled={disabled || field.disabled}
            {...rest}
          />
        );
      case "radio":
        return (
          <FormRadio
            name={field.input_name ?? "field_name"}
            required={field.required ?? false}
            type={field.input_type}
            label={field.input_label}
            direction={field.direction ?? "horizontal"}
            slug={field?.dropdown_slug}
            radioOptions={field.dropdown_options}
            disabled={disabled || field.disabled}
            defaultValue={field.input_value ?? null}
            {...rest}
          />
        );
      case "tree_select":
        return (
          <FormTreeSelect
            name={field?.input_name}
            slug={field?.dropdown_slug}
            placeholder={field.input_placeholder ?? ""}
            extra={field.extra ?? ""}
            disabled={disabled || field.disabled}
            {...rest}
          />
        );
      case "unit_hierarchy_cost_center":
        return <UnitHierarchy field={field} {...rest} />;
      case "unit_hierarchy_bud_budget":
        return <UnitHierarchy level={7} field={field} {...rest} />;
      default:
        return (
          <FormInput
            name={field.input_name}
            type="text"
            placeholder={field.input_placeholder ?? ""}
            disabled={disabled || field.disabled}
            {...rest}
          />
        );
    }
  };

  return (
    <Col span={field.element_column ?? 6}>
      <FormItemArea>
        <Form.Item
          name={field.input_name}
          label={
            field.label_class ? (
              <label className={field.label_class ?? false}>
                {field.label_name ?? false}
              </label>
            ) : (
              field.label_name ?? false
            )
          }
          rules={validRules}
          labelCol={labelCol ?? { span: 24 }}
          wrapperCol={wrapperCol ?? { span: 24 }}
          className={rest?.extraClass ?? ""}
        >
          {renderSwitch(field.input_type)}
        </Form.Item>
      </FormItemArea>
    </Col>
  );
};

export default FormItem;
